import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Eye, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import PageHeader from "@/components/admin/PageHeader";
import StatusBadge from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/button";

const PAGE_SIZE = 20;
const STATUSES = ["pending", "assigned", "scheduled", "completed", "cancelled", "rejected"];

export default function AdminConsultations() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const loadConsultations = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/consultations", { params });
      setItems(data.consultations);
      setTotal(data.total);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [page, debouncedSearch, statusFilter]);

  useEffect(() => { loadConsultations(); }, [loadConsultations]);

  const exportCsv = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/consultations/export/csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "consultations.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <AdminGuard>
      <AdminLayout>
        <PageHeader
          title="Consultations"
          subtitle={`${total} total consultations`}
          actions={
            <Button onClick={exportCsv} variant="outline" size="sm" className="rounded-full h-9 font-heading uppercase tracking-wider text-xs border-[hsl(var(--zt-teal))]/20 text-[hsl(var(--zt-teal))]">
              <Download size={13} className="mr-1.5" /> Export CSV
            </Button>
          }
        />

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-4 mb-6 flex flex-wrap gap-3 items-center shadow-sm">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--zt-charcoal))]/40" />
            <input
              type="text"
              placeholder="Search subject, user or email…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-4 h-9 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 bg-[hsl(var(--zt-beige))]/50"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="h-9 px-3 text-xs border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 bg-[hsl(var(--zt-beige))]/50 font-heading uppercase tracking-wider"
          >
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[700px]">
              <thead>
                <tr className="border-b border-[hsl(var(--zt-beige-deep))] bg-[hsl(var(--zt-beige))]/40">
                  {["Subject", "User", "Advisor", "Status", "Created", "Actions"].map(h => (
                    <th key={h} className="px-4 py-3.5 font-heading uppercase tracking-[0.15em] text-[9px] text-[hsl(var(--zt-charcoal))]/45 font-bold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <tr key={i} className="border-b border-[hsl(var(--zt-beige-deep))]/50 animate-pulse">
                      {[...Array(6)].map((_, j) => (
                        <td key={j} className="px-4 py-4"><div className="h-4 bg-[hsl(var(--zt-beige-deep))]/50 rounded" /></td>
                      ))}
                    </tr>
                  ))
                ) : items.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-[hsl(var(--zt-charcoal))]/40">No consultations found</td></tr>
                ) : items.map(c => (
                  <tr key={c.consultation_id} className="border-b border-[hsl(var(--zt-beige-deep))]/50 hover:bg-[hsl(var(--zt-beige))]/30 transition-colors">
                    <td className="px-4 py-3.5">
                      <p className="font-semibold text-sm text-[hsl(var(--zt-teal))] max-w-[200px] truncate">{c.subject}</p>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-sm text-[hsl(var(--zt-charcoal))]/80">{c.user_name || "—"}</p>
                      <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/50">{c.user_email}</p>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-[hsl(var(--zt-charcoal))]/65">{c.advisor_name || <span className="text-[hsl(var(--zt-charcoal))]/35 italic text-xs">Unassigned</span>}</td>
                    <td className="px-4 py-3.5"><StatusBadge status={c.status} /></td>
                    <td className="px-4 py-3.5 text-[11px] text-[hsl(var(--zt-charcoal))]/55 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                    <td className="px-4 py-3.5">
                      <button
                        onClick={() => navigate(`/admin/consultations/${c.consultation_id}`)}
                        className="w-7 h-7 rounded-lg bg-[hsl(var(--zt-beige))] grid place-items-center text-[hsl(var(--zt-teal))]/60 hover:text-[hsl(var(--zt-teal))] hover:bg-[hsl(var(--zt-beige-deep))] transition-colors"
                      >
                        <Eye size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-6 py-4 flex items-center justify-between border-t border-[hsl(var(--zt-beige-deep))]">
              <p className="text-xs text-[hsl(var(--zt-charcoal))]/50 font-heading">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="w-8 h-8 rounded-lg border border-[hsl(var(--zt-beige-deep))] grid place-items-center disabled:opacity-40 hover:bg-[hsl(var(--zt-beige))] transition-colors">
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs font-heading font-semibold text-[hsl(var(--zt-teal))]">{page} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="w-8 h-8 rounded-lg border border-[hsl(var(--zt-beige-deep))] grid place-items-center disabled:opacity-40 hover:bg-[hsl(var(--zt-beige))] transition-colors">
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
